import { call, delay, put, race, select, take, takeEvery } from 'redux-saga/effects';

import { getEnvironmentConfig, httpClientInterface } from 'app-components/utils/core';

import { resourceSagaUtils, resourceStoreTypes } from '../core';
import type { CoreActionCreator, QcsSyncResourcesConfigProps } from '../core/resourceStoreTypes';

import type { SagaIterator } from 'redux-saga';
import { getCreditReport } from '../credit-report/actions';
import * as actions from './actions';
import { getIsPolling } from './selectors';
import { type CreditProfileType, mkCreditProfile } from './types';

const pollingCompletedStatuses = [
  'AUTHENTICATION_COMPLETED',
  'AUTHENTICATION_FAILED',
  'REGISTRATION_COMPLETED',
  'REGISTRATION_FAILED',
];

const mkQcsSyncCreditProfileConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<CreditProfileType>();

export const mkResourceConfig = (
  successAction: CoreActionCreator<any, any>,
  failureAction: CoreActionCreator<any, any>,
  query = '',
  transformResourceWithResponse: QcsSyncResourcesConfigProps<CreditProfileType>['transformResourceWithResponse'] = (
    resource,
    response,
  ) => mkCreditProfile({ ...response.data, ...resource }),
) =>
  mkQcsSyncCreditProfileConfig({
    resourceName: 'credit-profile-registrations',
    resourceBaseUrl: `/creditscore/credit-profile-registrations${query}`,
    successAction,
    failureAction,
    transformResponseToResources: (response: any) =>
      response?.data?.map((creditProfile: CreditProfileType) => mkCreditProfile(creditProfile)),
    transformResourceWithResponse,
  });

export function* getCreditProfileActionWatcher() {
  const config = mkResourceConfig(actions.getCreditProfileSuccess, actions.getCreditProfileFailure);

  yield takeEvery(actions.getCreditProfile.type, resourceSagaUtils.qcsSyncGetResources<CreditProfileType>, config);
}

export function* createCreditProfileActionWatcher() {
  const config = mkResourceConfig(actions.createCreditProfileSuccess, actions.createCreditProfileFailure);
  yield takeEvery(actions.createCreditProfile.type, resourceSagaUtils.qcsSyncCreateResource<CreditProfileType>, config);
}

export function* registerCreditProfileActionWatcher() {
  yield takeEvery(actions.registerCreditProfile, function* handle(action) {
    const { creditProfileRegistrationId } = action.payload;
    const config = mkResourceConfig(
      actions.registerCreditProfileSuccess,
      actions.registerCreditProfileFailure,
      `/${creditProfileRegistrationId}/continue`,
    );
    yield call(resourceSagaUtils.qcsSyncCreateResource<CreditProfileType>, config, action);
  });
}

type PollActions =
  | ReturnType<typeof actions.pollRegisterCreditProfile>
  | ReturnType<typeof actions.resendVerificationRetrySuccess>;

export function* triggerPollCreditProfile(action: PollActions): SagaIterator {
  let stopPolling = false;
  while (!stopPolling) {
    const result = yield race({
      stop: take(actions.resendVerificationRetrySuccess),
      polling: call(pollRegistrationCheckApi, action),
    });
    if (result.stop || (result.polling && result.polling.stop)) {
      stopPolling = true;
    }
  }
}

function* pollRegistrationCheckApi(action: PollActions): SagaIterator {
  const MAX_ATTEMPTS = 48;
  const DELAY_TIME = 5000; // 5 * 48 = 240sec (4 minutes)

  let idParam;
  if ('creditProfileRegistrationId' in action.payload) {
    idParam = action.payload.creditProfileRegistrationId;
  } else if ('id' in action.payload) {
    idParam = action.payload.id as string;
  }
  const requestUrl = `${getEnvironmentConfig().services_url}/creditscore/credit-profile-registrations/${idParam}`;
  let attempts = 0;
  let stopPolling = false;
  while (attempts < MAX_ATTEMPTS && !stopPolling) {
    const isPolling = yield select(getIsPolling);
    if (!isPolling) {
      stopPolling = true;
      break;
    }
    try {
      const { data } = yield call(httpClientInterface.send, 'GET', requestUrl);
      if (pollingCompletedStatuses.includes(data.status)) {
        stopPolling = true; // Set flag to stop polling
        const transformedData = [
          mkCreditProfile({
            ...data,
            creditProfile: {
              ...data.creditProfile,
              ssn: 'ssn' in action.payload ? action.payload.ssn : undefined,
            },
          }),
        ];
        yield put(actions.pollRegisterCreditProfileSuccess({ resources: transformedData, doMerge: true }));
        break;
      }
      if (data.error) {
        stopPolling = true;
        yield put(actions.registerCreditProfileFailure(data));
        break;
      }
    } catch (error: any) {
      stopPolling = true;
      yield put(actions.registerCreditProfileFailure(error));
      break;
    }
    attempts++;
    yield delay(DELAY_TIME);
  }
  if (attempts === MAX_ATTEMPTS) {
    stopPolling = true;
    yield put(
      actions.verifyCreditProfileFailure({ message: `Credit Registration Polling Stopped After ${attempts} Attempts` }),
    );
  }
  return { stop: stopPolling };
}

export function* pollCreditProfileActionWatcher() {
  yield takeEvery(
    [actions.pollRegisterCreditProfile, actions.resendVerificationRetrySuccess],
    triggerPollCreditProfile,
  );
}

export function* resendVerificationRetryActionWatcher() {
  yield takeEvery(actions.resendVerificationRetry, function* handle(action) {
    const { creditProfileRegistrationId } = action.payload;
    const config = mkResourceConfig(
      actions.resendVerificationRetrySuccess,
      actions.resendVerificationRetryFailure,
      `/${creditProfileRegistrationId}/retry`,
      (_resource, response) => ({
        ...response.data,
        creditProfile: {
          ...response.data?.creditProfile,
          ssn: action.payload.ssn,
        },
      }),
    );
    yield call(resourceSagaUtils.qcsSyncCreateResource<CreditProfileType>, config, action);
  });
}

export function* deleteCreditProfileActionWatcher() {
  yield takeEvery(actions.deleteCreditProfile, function* handle(action) {
    const { creditProfileRegistrationId } = action.payload;
    const config = mkResourceConfig(
      actions.deleteCreditProfileSuccess,
      actions.deleteCreditProfileFailure,
      `/${creditProfileRegistrationId}`,
      (_resource, response) => ({ ...response.data, id: creditProfileRegistrationId }),
    );
    yield call(resourceSagaUtils.qcsSyncDeleteResource<CreditProfileType>, config, action);
  });
}

export function* listenGetProfileSuccessActionWatcher() {
  yield takeEvery(actions.getCreditProfileSuccess, function* handle(action): Generator<any, any, any> {
    if (action.payload.resources.length) {
      const { id, status } = action.payload.resources[0];
      if (status !== 'CREDIT_REPORT_RECEIVED') {
        const config = mkResourceConfig(
          actions.deleteCreditProfileSuccess,
          actions.deleteCreditProfileFailure,
          `/${id}`,
          (_resource, response) => ({ ...response.data, id }),
        );
        const isPolling = yield select(getIsPolling);
        // Check if polling is in progress because of registration process.
        // If yes, do not delete the profile as registration process and score generation is not yet concluded.
        if (!isPolling) {
          yield call(resourceSagaUtils.qcsSyncDeleteResource<CreditProfileType>, config, action);
        }
      } else {
        yield put(getCreditReport());
      }
    }
  });
}

export default [
  getCreditProfileActionWatcher,
  createCreditProfileActionWatcher,
  registerCreditProfileActionWatcher,
  pollCreditProfileActionWatcher,
  resendVerificationRetryActionWatcher,
  deleteCreditProfileActionWatcher,
  listenGetProfileSuccessActionWatcher,
];
